import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { SearchInput } from '@/components/shared/SearchInput';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Pagination } from '@/components/shared/Pagination';
import { Skeleton } from '@/components/ui/skeleton';
import { useGetCompanies, useDeleteCompany } from '@/services/api/endpoints/CompanyService';
import { useDebounce } from '@/hooks/useDebounce';
import { formatDate } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { CompanyModal } from './CompanyModal';
import type { Company } from '@/types';

export function CompaniesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);

  const { data, isLoading } = useGetCompanies(page, 20, debouncedSearch, 'name', 'asc');
  const deleteMutation = useDeleteCompany();

  const handleCreate = () => {
    setEditingCompany(null);
    setModalOpen(true);
  };

  const handleEdit = (e: React.MouseEvent, company: Company) => {
    e.stopPropagation();
    setEditingCompany(company);
    setModalOpen(true);
  };

  const handleDelete = async (e: React.MouseEvent, company: Company) => {
    e.stopPropagation();
    const confirmed = window.confirm(t('companies.deleteConfirm'));
    if (!confirmed) return;

    try {
      await deleteMutation.mutateAsync(company.id);
      toast.success(t('companies.toast.deleteSuccess'));
    } catch {
      toast.error(t('companies.toast.deleteError'));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t('companies.title')}</h1>
        <Button className="gap-2" onClick={handleCreate}>
          <Plus className="h-4 w-4" />
          {t('companies.createCompany')}
        </Button>
      </div>

      {/* Search */}
      <div className="max-w-sm">
        <SearchInput value={search} onChange={setSearch} />
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12" />
              ))}
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">{t('companies.name')}</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">{t('companies.email')}</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">{t('companies.phone')}</th>
                      <th className="text-center p-4 text-sm font-medium text-muted-foreground">{t('companies.machines')}</th>
                      <th className="text-center p-4 text-sm font-medium text-muted-foreground">{t('companies.users')}</th>
                      <th className="text-center p-4 text-sm font-medium text-muted-foreground">{t('common.status')}</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Datum</th>
                      <th className="text-right p-4 text-sm font-medium text-muted-foreground">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.items.map((company) => (
                      <tr
                        key={company.id}
                        className="border-b hover:bg-muted/30 cursor-pointer transition-colors"
                        onClick={() => navigate(`/companies/${company.id}`)}
                      >
                        <td className="p-4">
                          <div className="font-medium">{company.name}</div>
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">{company.email || '-'}</td>
                        <td className="p-4 text-sm text-muted-foreground">{company.phone || '-'}</td>
                        <td className="p-4 text-center text-sm">{company.machineCount ?? '-'}</td>
                        <td className="p-4 text-center text-sm">{company.userCount ?? '-'}</td>
                        <td className="p-4 text-center">
                          <StatusBadge status={company.status} />
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">{formatDate(company.createdAt)}</td>
                        <td className="p-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => handleEdit(e, company)}
                              title={t('common.edit')}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => handleDelete(e, company)}
                              title={t('common.delete')}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {data?.items.length === 0 && (
                      <tr>
                        <td colSpan={8} className="p-8 text-center text-muted-foreground">
                          {t('common.noResults')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden divide-y">
                {data?.items.map((company) => (
                  <div
                    key={company.id}
                    className="p-4 space-y-2 active:bg-muted/30 cursor-pointer"
                    onClick={() => navigate(`/companies/${company.id}`)}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{company.name}</span>
                      <StatusBadge status={company.status} />
                    </div>
                    <div className="text-sm text-muted-foreground">{company.email || '-'}</div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        {t('companies.machines')}: {company.machineCount ?? '-'} &middot; {t('companies.users')}: {company.userCount ?? '-'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">{formatDate(company.createdAt)}</span>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={(e) => handleEdit(e, company)}
                          title={t('common.edit')}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={(e) => handleDelete(e, company)}
                          title={t('common.delete')}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {data?.items.length === 0 && (
                  <div className="p-8 text-center text-muted-foreground">
                    {t('common.noResults')}
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {data?.pagination && (
        <Pagination
          page={page}
          totalPages={data.pagination.totalPages}
          onPageChange={setPage}
        />
      )}

      {/* Company Modal */}
      <CompanyModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        company={editingCompany}
      />
    </div>
  );
}
